import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BoxAd - 300x250 sidebar ad component
 * Displays ads assigned to 'sidebar' or 'box_ad' placements
 * Shows random ad based on priority weighting
 */
export const BoxAd = ({ placement = "sidebar", className = "" }) => {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const response = await axios.get(`${API}/ads/placement/${placement}`);
        const ads = response.data || [];
        
        if (ads.length === 0) {
          setAd(null);
          return;
        }
        
        // Select ad based on priority weighting
        // Higher priority = higher chance of being shown
        const totalWeight = ads.reduce((sum, ad) => sum + (ad.priority || 1), 0);
        let random = Math.random() * totalWeight;
        
        for (const adItem of ads) {
          random -= (adItem.priority || 1);
          if (random <= 0) {
            setAd(adItem);
            return;
          }
        }
        
        // Fallback to first ad
        setAd(ads[0]);
      } catch (error) {
        console.error("Error fetching box ad:", error);
        setAd(null);
      }
    };

    fetchAd();
  }, [placement]);

  const handleClick = async () => {
    if (!ad) return;
    
    try {
      await axios.post(`${API}/ads/click/${ad.id}`);
    } catch (error) {
      console.error("Error tracking ad click:", error);
    }

    if (ad.link_url) {
      if (ad.link_url.startsWith('http')) {
        window.open(ad.link_url, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = ad.link_url;
      }
    }
  };

  if (!ad) return null;

  return (
    <div 
      className={`bg-zinc-900 border border-zinc-800 ${className}`}
      data-testid={`box-ad-${placement}`}
    >
      <div
        onClick={handleClick}
        className={`relative ${ad.link_url ? 'cursor-pointer' : ''}`}
        style={{ width: '300px', height: '250px' }}
      >
        <img
          src={ad.image_url}
          alt={ad.name}
          className="w-full h-full object-cover"
        />
        <span className="absolute top-1 right-1 text-[8px] font-bold uppercase tracking-widest text-white/40 bg-black/40 px-1 py-0.5">
          AD
        </span>
      </div>
    </div>
  );
};

/**
 * SidebarAds - Stack of box ads for sidebar placement
 * Can show multiple ads stacked vertically
 */
export const SidebarAds = ({ count = 2, className = "", placement = "sidebar" }) => {
  const [ads, setAds] = useState([]);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await axios.get(`${API}/ads/placement/${placement}`);
        const allAds = response.data || [];
        
        if (allAds.length === 0) {
          setAds([]);
          return;
        }
        
        // Shuffle based on priority and select up to 'count' ads
        const shuffled = [...allAds].sort((a, b) => {
          const aWeight = (a.priority || 1) * Math.random();
          const bWeight = (b.priority || 1) * Math.random();
          return bWeight - aWeight;
        });
        
        setAds(shuffled.slice(0, count));
      } catch (error) {
        console.error("Error fetching sidebar ads:", error);
        setAds([]);
      }
    };

    fetchAds();
  }, [count]);

  if (ads.length === 0) return null;

  return (
    <div className={`space-y-4 ${className}`}>
      {ads.map((ad) => (
        <BoxAdItem key={ad.id} ad={ad} />
      ))}
    </div>
  );
};

// Internal component for rendering a single box ad
const BoxAdItem = ({ ad }) => {
  const handleClick = async () => {
    try {
      await axios.post(`${API}/ads/click/${ad.id}`);
    } catch (error) {
      console.error("Error tracking ad click:", error);
    }

    if (ad.link_url) {
      if (ad.link_url.startsWith('http')) {
        window.open(ad.link_url, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = ad.link_url;
      }
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800">
      <div
        onClick={handleClick}
        className={`relative ${ad.link_url ? 'cursor-pointer' : ''}`}
        style={{ width: '300px', height: '250px' }}
      >
        <img
          src={ad.image_url}
          alt={ad.name}
          className="w-full h-full object-cover"
        />
        <span className="absolute top-1 right-1 text-[8px] font-bold uppercase tracking-widest text-white/40 bg-black/40 px-1 py-0.5">
          AD
        </span>
      </div>
    </div>
  );
};

export default BoxAd;
